import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { HistogramBinData } from '@/lib/types/ds/prediction-error-evaluation';

interface ErrorHistogramChartProps {
  title: string;
  data: HistogramBinData;
  useLogScale?: boolean;
  /** p1/p99 の参照線を表示するか（デフォルト: true） */
  showPercentileLines?: boolean;
}

interface BinDataPoint {
  binStart: number;
  binEnd: number;
  binLabel: string;
  count: number;
  /** ログスケール用: log10(count) + 0.5。count=0 の場合は 0（高さゼロ） */
  logCount: number;
}

/** +0.5 オフセットにより count=1 (logCount=0.5) が表示可能になる */
const LOG_OFFSET = 0.5;

function toBinDataPoints(data: HistogramBinData): BinDataPoint[] {
  const points: BinDataPoint[] = [];
  for (let i = 0; i < data.counts.length; i++) {
    const binStart = data.bins[i];
    const binEnd = data.bins[i + 1];
    if (binStart === undefined || binEnd === undefined) continue;
    const count = data.counts[i];
    points.push({
      binStart,
      binEnd,
      binLabel: `${binStart.toFixed(1)}`,
      count,
      logCount: count > 0 ? Math.log10(count) + LOG_OFFSET : 0,
    });
  }
  return points;
}

/** 指定値を含むビンのラベルを返す */
function findBinLabel(binData: BinDataPoint[], value: number): string | undefined {
  const bin = binData.find((d) => value >= d.binStart && value < d.binEnd);
  // 最終ビンは閉区間なので、見つからなければ最後のビンを確認
  if (!bin && binData.length > 0) {
    const last = binData[binData.length - 1];
    if (value >= last.binStart && value <= last.binEnd) return last.binLabel;
  }
  return bin?.binLabel;
}

/** バーの色: 負（不足予測）= オレンジ、正（過予測）= 青 */
function getBarColor(binStart: number, binEnd: number): string {
  const center = (binStart + binEnd) / 2;
  if (center < 0) return '#f97316'; // orange-500
  return '#42a5f5'; // blue
}

export function ErrorHistogramChart({
  title,
  data,
  useLogScale = false,
  showPercentileLines = true,
}: ErrorHistogramChartProps) {
  if (!data || data.counts.length === 0) {
    return null;
  }

  const binData = toBinDataPoints(data);
  const dataKey = useLogScale ? 'logCount' : 'count';

  // ログスケールのY軸: ティック位置は 0.5, 1.5, 2.5, ... → ラベルは 10^0, 10^1, 10^2, ...
  const maxPower = Math.ceil(
    Math.max(...binData.filter((d) => d.count > 0).map((d) => Math.log10(d.count)), 0),
  );
  const logTicks = Array.from({ length: maxPower + 1 }, (_, i) => i + LOG_OFFSET);

  // 参照線のビンラベルを取得
  const zeroBinLabel = findBinLabel(binData, 0);
  const p1BinLabel = showPercentileLines ? findBinLabel(binData, data.p1) : undefined;
  const p99BinLabel = showPercentileLines ? findBinLabel(binData, data.p99) : undefined;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span>N = {data.n.toLocaleString()}</span>
          <span>中央値 = {data.median.toFixed(2)}</span>
          <span>SD = {data.std.toFixed(2)}</span>
          <span>
            98%区間 [{data.p1.toFixed(2)}, {data.p99.toFixed(2)}]
          </span>
        </div>
        <div className="flex gap-3 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#f97316]" />
            不足予測 (予測 &lt; 実績)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#42a5f5]" />
            過予測 (予測 &gt; 実績)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-0 w-4 border-t-2 border-dashed border-[#ef4444]" />
            誤差=0
          </span>
          {showPercentileLines && (
            <span className="flex items-center gap-1">
              <span className="inline-block h-0 w-4 border-t-2 border-dashed border-[#3b82f6]" />
              1%/99%点
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={binData} margin={{ top: 5, right: 30, left: 20, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="binLabel"
              label={{ value: '誤差 (予測 - 実績)', position: 'insideBottom', offset: -20 }}
              tick={{ fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={useLogScale ? [0, maxPower + LOG_OFFSET] : [0, 'auto']}
              ticks={useLogScale ? logTicks : undefined}
              tickFormatter={
                useLogScale
                  ? (v: number) => Math.pow(10, v - LOG_OFFSET).toLocaleString()
                  : (v: number) => v.toLocaleString()
              }
              allowDecimals={false}
              label={{
                value: useLogScale ? '頻度 (log scale)' : '頻度',
                angle: -90,
                position: 'insideLeft',
                offset: -5,
              }}
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              formatter={(_value: number, _name: string, props: { payload?: BinDataPoint }) => [
                (props.payload?.count ?? 0).toLocaleString(),
                '件数',
              ]}
              labelFormatter={(_label: string, payload: { payload?: BinDataPoint }[]) => {
                const d = payload?.[0]?.payload;
                if (d) return `区間: ${d.binStart.toFixed(1)} ~ ${d.binEnd.toFixed(1)}`;
                return `区間: ${_label}`;
              }}
            />
            <Bar dataKey={dataKey}>
              {binData.map((d, i) => (
                <Cell key={i} fill={getBarColor(d.binStart, d.binEnd)} />
              ))}
            </Bar>
            {zeroBinLabel && (
              <ReferenceLine
                x={zeroBinLabel}
                stroke="#ef4444"
                strokeDasharray="5 5"
                label={{ value: '0', position: 'top', fontSize: 10, fill: '#ef4444' }}
              />
            )}
            {p1BinLabel && (
              <ReferenceLine
                x={p1BinLabel}
                stroke="#3b82f6"
                strokeDasharray="5 5"
                label={{ value: '1%', position: 'top', fontSize: 10, fill: '#3b82f6' }}
              />
            )}
            {p99BinLabel && (
              <ReferenceLine
                x={p99BinLabel}
                stroke="#3b82f6"
                strokeDasharray="5 5"
                label={{ value: '99%', position: 'top', fontSize: 10, fill: '#3b82f6' }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
